import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SmartBookingLayout } from '../components/SmartBookingLayout';
import { RootState } from '../redux/store';
import { StepCard } from '../components/StepCard';
import { Button } from '../components/Button';
import { User, Clock, Phone, Mail, Stethoscope, NotebookPen, BadgeCheck, Pill, AlertCircle } from 'lucide-react';
import { createAppointment, getUserFacingErrorMessage } from '../services/api';
import { trackCurve } from '../utils/curve';
import { SuccessCard } from '../components/SuccessCard';
import { SuccessWithoutPolling } from '../components/SuccessWithoutPolling';
import { formatSelectedSlot, hasRequiredNotes } from '../utils/helpers';
import { PatientTypeEnum } from '../types';
import { resetAppointment, resolvePatient, setPatientDetails, setSelectedSlot } from '../redux/slices/appointmentSlice';
import { AppointmentNotesField } from '../components/patient-details/AppointmentNotesField';
import { useReferralToken } from '../hooks/useReferralToken';

const BOOKING_FAILED_MESSAGE = "We couldn't book your appointment. Please try again.";
// Backend wording when the slot was booked between selection and confirm; only then is picking another slot the fix
const SLOT_UNAVAILABLE_ERROR = 'already been taken';

export const Confirmation = () => {
  const { treatment, patientType, selectedSlot, patientDetails, existingPatientMatch, patient_request_id, request_id, preferredTimeSlots } = useSelector((s: RootState) => s.appointment);
  const patient = resolvePatient(patientDetails, existingPatientMatch);
  const { practice } = useSelector((s: RootState) => s.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const referralToken = useReferralToken();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ time: string; patientName: string; requestId: string | null } | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const isSlotUnavailable = !!bookingError?.includes(SLOT_UNAVAILABLE_ERROR);
  const isExistingPatient = patientType === PatientTypeEnum.Existing;

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isSmartBooking = useMemo(() => {
    const value = searchParams.get('smart_booking');
    return value === 'true' || value === '1';
  }, [searchParams]);

  const isManual = practice?.scheduler_type === 'manual';
  const showPortalLink = !!practice?.show_portal_link;

  const Container = isSmartBooking ? SmartBookingLayout : Layout;
  // Smart booking skips the patient details step, so notes are collected here
  const collectNotes = isSmartBooking;
  const notesValid = hasRequiredNotes(treatment?.notesRequired, patient.notes);

  const book = async () => {
    if (!treatment || !patientType || !notesValid) return;
    if (!isManual && !selectedSlot) return;
    if (isManual && preferredTimeSlots.length === 0) return;

    const payload: any = {
      patient_request_id: patient_request_id,
      appointment_type_id: treatment.id,
      reason: patient.notes || '',
    };

    if (isManual) {
      payload.appointment_time_preferences_attributes = preferredTimeSlots.map(slot => ({
        preferred_date: slot.preferred_date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        priority: slot.priority,
      }));
    } else {
      payload.appointment_at = selectedSlot;
    }

    setBookingError(null);
    setSubmitting(true);

    // Only the request itself is guarded: once it succeeds, nothing below may report a failure
    try {
      await createAppointment(payload as any, referralToken);
    } catch (error) {
      setBookingError(getUserFacingErrorMessage(error, BOOKING_FAILED_MESSAGE));
      return;
    } finally {
      setSubmitting(false);
    }

    trackCurve('appointment_requested', {
      appointment_type: treatment.slug,
      patient_type: patientType,
      scheduler_type: isManual ? 'manual' : 'integrated',
      smart_booking: isSmartBooking,
    });
    setSuccess({
      time: selectedSlot!,
      patientName: `${patient.firstName} ${patient.lastName}`,
      requestId: request_id
    });
    if (isSmartBooking) {
      dispatch(setSelectedSlot(null));
    } else {
      dispatch(resetAppointment());
    }
  };

  const handleBack = () => {
    if (isSmartBooking) {
      navigate(`../smart-booking${location.search || ''}`);
    } else {
      navigate('../step-4');
    }
  };

  const handleSelectAnotherSlot = () => {
    if (isSmartBooking) {
      navigate(`../smart-booking${location.search || ''}`);
    } else {
      navigate('../step-3');
    }
  };

  return (
    <Container>
      <div className="space-y-4">
        <div className='flex flex-col items-center text-center md:items-start md:text-start'>
          <h1 className="text-2xl font-semibold text-black">Confirmation</h1>
          <p className="text-sm text-gray-500">Thank you for taking the time to book with us. Please review your information below.</p>
        </div>

        {bookingError && (
          <StepCard>
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Booking Failed</h3>
              <p className="text-gray-600 mb-6">{bookingError}</p>
              {isSlotUnavailable ? (
                <Button title="Select Another Slot" onClick={handleSelectAnotherSlot} />
              ) : (
                <p className="text-sm text-gray-600">
                  Please call us{practice?.phone_number && <> at <a href={`tel:${practice.phone_number}`} className="font-medium text-black">{practice.phone_number}</a></>} and we'll help you book.
                </p>
              )}
            </div>
          </StepCard>
        )}

        {!bookingError && (
          <StepCard className='overflow-y-auto'>
            {success ? (
              showPortalLink
                ? <SuccessCard isSmartBooking={isSmartBooking} success={success} />
                : <SuccessWithoutPolling isSmartBooking={isSmartBooking} success={success} />
            ) : (
              <div className="space-y-5 py-2">
                {/* Treatment & Patient Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Stethoscope size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Treatment</div>
                      <div className="text-base font-medium text-black mt-1">{treatment?.title}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User size={20} className="text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Patient Type</div>
                      <div className="text-base font-medium text-black mt-1 capitalize">{patientType || '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Time Slot */}
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">
                        {isManual ? 'Preferred Time' : 'Appointment Time'}
                      </div>
                      <div className="text-base font-medium text-black">
                        {isManual ? (
                          <div className="space-y-1">
                            {preferredTimeSlots.map((slot, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="font-semibold">{idx === 0 ? 'Preferred: ' : 'Alternate: '}</span>
                                {slot.preferred_date ? new Date(slot.preferred_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : ''} {slot.any_time ? '(Any Time)' : `${slot.start_time?.split(' ')[1]} ${slot.start_time?.split(' ')[2]} - ${slot.end_time?.split(' ')[1]} ${slot.end_time?.split(' ')[2]}`}
                              </div>
                            ))}
                          </div>
                        ) : (
                          formatSelectedSlot(selectedSlot || '')
                        )}
                      </div>
                    </div>
                  </div>
                  {!collectNotes && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <NotebookPen size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Appointment Note</div>
                        <div className="text-sm font-medium text-black mt-1">{patient.notes || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>

                {collectNotes && (
                  <AppointmentNotesField
                    rows={1}
                    value={patientDetails.notes}
                    onChange={(notes) => dispatch(setPatientDetails({ ...patientDetails, notes }))}
                  />
                )}

                {/* Divider */}
                <div className="border-t border-gray-100"></div>

                {/* Contact Information */}
                <div>
                  <div className='flex gap-3'>
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Contact Information</h3>
                    {isExistingPatient &&
                      <div className='flex justify-center item-center badge badge-success gap-1'>
                        <BadgeCheck size={16} fill='white' className='text-success-600' />
                        <p className='text-white text-xs font-semibold'>Record Found</p>
                      </div>
                    }
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <User size={18} className="text-gray-400" />
                      <div>
                        <div className="text-xs text-gray-500">Name</div>
                        <div className="text-sm font-medium text-black">
                          {patient.firstName} {patient.lastName}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Phone size={18} className="text-gray-400" />
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="text-sm font-medium text-black">{patient.phone || '-'}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 md:col-span-2">
                      <Mail size={18} className="text-gray-400" />
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="text-sm font-medium text-black">{patient.email || '-'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </StepCard>
        )}

        {/* Action Buttons */}
        {!success && !bookingError && (
          <div className="flex justify-between">
            <Button title='Back' variant='secondary' onClick={handleBack} />
            <Button
              title={submitting ? (isManual ? 'Submitting…' : 'Booking…') : (isManual ? 'Submit Request' : 'Confirm Appointment')}
              disabled={submitting || !notesValid}
              onClick={book}
              className='text-sm sm:text-[16px]'
            />
          </div>
        )}
      </div>
    </Container>
  );
};